// The session's first landing stays in session storage as pt_first_touch for the checkout, and a compact copy goes
// into the pt_touches cookie, which the app drains at its next boot. Compact copies cut values to caps for Paddle's and
// the browser's bounds. A cookie entry that alone runs over the cookie cap is cut to the smaller caps, as at checkout.
import { getPostHogIdentity } from "../tracking/tracking-service"
import { getFacebookCookies, getIpAddress } from "./meta-ads-utils"
import { readCookie } from "./read-cookie"

export interface LandingTouch {
  readonly occurred_at: number
  readonly host: string
  readonly landing_url: string
  readonly referrer?: string
  readonly utm_source?: string
  readonly utm_medium?: string
  readonly utm_campaign?: string
  readonly utm_content?: string
  readonly utm_term?: string
  readonly fbclid?: string
  readonly gclid?: string
  readonly ttclid?: string
  readonly msclkid?: string
  readonly posthog_distinct_id?: string
  readonly posthog_session_id?: string
  readonly fbp?: string
  readonly fbc?: string
  readonly ip?: string
  readonly user_agent?: string
}

export interface LandingInput {
  readonly url: string
  readonly referrer: string
  readonly host: string
  readonly now: number
  readonly cookies: { readonly fbp?: string; readonly fbc?: string }
  readonly identity: { readonly distinctId?: string; readonly sessionId?: string }
}

const MAX_VALUE_LENGTH = 1024
const COMPACT_VALUE_LENGTH = 100
const USER_AGENT_CAP_FACTOR = 2
const CHECKOUT_JSON_MAX_LENGTH = 2048
const SECOND_VALUE_LENGTH = 50
const LAST_VALUE_LENGTH = 25
const FALLBACK_CAPS = [SECOND_VALUE_LENGTH, LAST_VALUE_LENGTH] as const
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const
const CLICK_ID_KEYS = ["fbclid", "gclid", "ttclid", "msclkid"] as const
const MS_PER_SECOND = 1000
const FIRST_TOUCH_KEY = "pt_first_touch"
const TOUCH_COOKIE = "pt_touches"
const TOUCH_COOKIE_MAX_ENTRIES = 5
const TOUCH_COOKIE_MAX_ENCODED_LENGTH = 3500
const TOUCH_COOKIE_MAX_AGE_SECONDS = 604_800
const SHARED_COOKIE_DOMAIN = "profittree.io"

const cut = (value: string | null | undefined): string | undefined =>
  value ? value.slice(0, MAX_VALUE_LENGTH) : undefined

export const buildLandingTouch = (input: LandingInput): LandingTouch => {
  const params = new URL(input.url).searchParams
  const touch: Record<string, string | number | undefined> = {
    occurred_at: Math.floor(input.now / MS_PER_SECOND),
    host: input.host,
    landing_url: cut(input.url),
    referrer: cut(input.referrer),
  }
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) touch[key] = cut(params.get(key))
  touch.posthog_distinct_id = cut(input.identity.distinctId)
  touch.posthog_session_id = cut(input.identity.sessionId)
  touch.fbp = cut(input.cookies.fbp)
  touch.fbc = cut(input.cookies.fbc)
  return Object.fromEntries(Object.entries(touch).filter(([, value]) => value !== undefined)) as unknown as LandingTouch
}

const compactTouch = (touch: LandingTouch, cap: number = COMPACT_VALUE_LENGTH): LandingTouch =>
  Object.fromEntries(
    Object.entries(touch).map(([key, value]) => [
      key,
      typeof value === "string" ? value.slice(0, key === "user_agent" ? cap * USER_AGENT_CAP_FACTOR : cap) : value,
    ])
  ) as unknown as LandingTouch

const encodeCompactTouch = (touch: LandingTouch, encode: (compact: LandingTouch) => string, maxLength: number): string => {
  let encoded = encode(compactTouch(touch))
  for (const cap of FALLBACK_CAPS) {
    if (encoded.length <= maxLength) return encoded
    encoded = encode(compactTouch(touch, cap))
  }
  return encoded
}

/** The touch as JSON of at most 2048 characters: values capped at 100, then 50, then 25 when it runs over. */
export const checkoutTouchJson = (touch: LandingTouch): string =>
  encodeCompactTouch(touch, (compact) => JSON.stringify(compact), CHECKOUT_JSON_MAX_LENGTH)

const readTouchCookie = (): LandingTouch[] => {
  const raw = readCookie(TOUCH_COOKIE)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as LandingTouch[]) : []
  } catch {
    return []
  }
}

const isSharedCookieHost = (hostname: string): boolean =>
  hostname === SHARED_COOKIE_DOMAIN || hostname.endsWith(`.${SHARED_COOKIE_DOMAIN}`)

const encodeTouches = (touches: readonly LandingTouch[]): string => encodeURIComponent(JSON.stringify(touches))

const writeTouchCookie = (touches: readonly LandingTouch[]): void => {
  let kept = touches.slice(-TOUCH_COOKIE_MAX_ENTRIES)
  let encoded = encodeTouches(kept)
  while (encoded.length > TOUCH_COOKIE_MAX_ENCODED_LENGTH && kept.length > 1) {
    kept = kept.slice(1)
    encoded = encodeTouches(kept)
  }
  if (encoded.length > TOUCH_COOKIE_MAX_ENCODED_LENGTH) {
    encoded = encodeCompactTouch(kept[0], (compact) => encodeTouches([compact]), TOUCH_COOKIE_MAX_ENCODED_LENGTH)
  }
  const domain = isSharedCookieHost(window.location.hostname) ? `; Domain=.${SHARED_COOKIE_DOMAIN}` : ""
  document.cookie = `${TOUCH_COOKIE}=${encoded}; Path=/; Max-Age=${TOUCH_COOKIE_MAX_AGE_SECONDS}; Secure; SameSite=Lax${domain}`
}

const writeFunnelTouchCookie = (touch: LandingTouch): void => {
  writeTouchCookie([...readTouchCookie(), compactTouch(touch)])
}

const replaceFunnelTouchCookieEntry = (touch: LandingTouch): void => {
  const touches = readTouchCookie()
  const isSameLanding = (entry: LandingTouch): boolean =>
    entry.posthog_session_id === touch.posthog_session_id && entry.host === touch.host
  if (!touches.some(isSameLanding)) return
  writeTouchCookie(touches.map((entry) => (isSameLanding(entry) ? compactTouch(touch) : entry)))
}

let unstoredFirstTouch: LandingTouch | undefined

/** The session's first touch from session storage, or from memory when the storage refused it. */
export const readFirstTouch = (): LandingTouch | undefined => {
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(FIRST_TOUCH_KEY)
  } catch {
    return unstoredFirstTouch
  }
  if (!raw) return unstoredFirstTouch
  try {
    return JSON.parse(raw) as LandingTouch
  } catch {
    return undefined
  }
}

const writeFirstTouch = (touch: LandingTouch): void => {
  try {
    window.sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch))
    unstoredFirstTouch = undefined
  } catch {
    unstoredFirstTouch = touch
  }
}

let landingIpLookup: Promise<void> = Promise.resolve()

/** Settles once this page's landing IP lookup settles, or after `timeoutMs`, whichever comes first. */
export const waitForLandingIp = (timeoutMs: number): Promise<void> =>
  Promise.race([landingIpLookup, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))])

const mergeIpAddress = async (sessionId: string | undefined): Promise<void> => {
  const ip = await getIpAddress()
  const stored = readFirstTouch()
  if (!ip || !stored || stored.posthog_session_id !== sessionId) return
  const withIp = { ...stored, ip }
  writeFirstTouch(withIp)
  replaceFunnelTouchCookieEntry(withIp)
}

/**
 * Stores this landing as the session's first touch unless one of the same PostHog session is already stored, and
 * writes it into the pt_touches cookie. The visitor's IP joins the stored touch once its lookup resolves.
 */
export const rememberLandingTouch = (): void => {
  const identity = getPostHogIdentity()
  const stored = readFirstTouch()
  const current =
    stored && stored.posthog_session_id === identity.sessionId
      ? stored
      : buildLandingTouch({
          url: window.location.href,
          referrer: document.referrer,
          host: window.location.host,
          now: Date.now(),
          cookies: getFacebookCookies(),
          identity,
        })
  if (current !== stored) {
    writeFirstTouch(current)
    writeFunnelTouchCookie(current)
  }
  if (!current.ip) landingIpLookup = mergeIpAddress(current.posthog_session_id)
}
